import React from "react";
import * as RDropdown from "@radix-ui/react-dropdown-menu";
import { Check } from "lucide-react";
import { cn } from "@/shared/lib/cn";

/**
 * DropdownMenu acessível (Radix) com estilo calmo do design system.
 *
 * Substitui menus custom feitos com <div absolute> + state manual, que
 * normalmente não têm navegação por teclado, ARIA nem foco gerenciado.
 *
 * Uso composável:
 *   <DropdownMenu trigger={<button>…</button>}>
 *     <DropdownItem onSelect={…}>Editar</DropdownItem>
 *     <DropdownSeparator />
 *     <DropdownItem variant="danger" onSelect={…}>Apagar</DropdownItem>
 *   </DropdownMenu>
 */

export function DropdownMenu({
    trigger,
    children,
    align = "end",
    className,
}: {
    trigger: React.ReactNode;
    children: React.ReactNode;
    align?: "start" | "center" | "end";
    className?: string;
}) {
    return (
        <RDropdown.Root>
            <RDropdown.Trigger asChild>{trigger}</RDropdown.Trigger>
            <RDropdown.Portal>
                <RDropdown.Content
                    align={align}
                    sideOffset={6}
                    className={cn(
                        "z-portal min-w-[10rem] max-w-[calc(100vw-2rem)] max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto rounded-lg border border-slate-200 dark:border-white/[0.08] bg-white dark:bg-night_surface shadow-lg p-1",
                        "data-[state=open]:animate-overlay-enter",
                        className
                    )}
                >
                    {children}
                </RDropdown.Content>
            </RDropdown.Portal>
        </RDropdown.Root>
    );
}

export function DropdownItem({
    children,
    onSelect,
    variant = "default",
    disabled,
    icon,
}: {
    children: React.ReactNode;
    onSelect?: () => void;
    variant?: "default" | "danger";
    disabled?: boolean;
    icon?: React.ReactNode;
}) {
    return (
        <RDropdown.Item
            disabled={disabled}
            onSelect={(e) => {
                e.preventDefault();
                onSelect?.();
            }}
            className={cn(
                "flex cursor-pointer select-none items-center gap-2.5 rounded-md px-3 py-2 touch:min-h-touch text-sm outline-none transition-colors",
                "data-[disabled]:opacity-40 data-[disabled]:cursor-not-allowed",
                variant === "danger"
                    ? "text-red-600 dark:text-red-400 data-[highlighted]:bg-red-50 dark:data-[highlighted]:bg-red-900/30"
                    : "text-slate-700 dark:text-zinc-200 data-[highlighted]:bg-slate-100 dark:data-[highlighted]:bg-night_input"
            )}
        >
            {icon && <span className="shrink-0 [&>svg]:w-4 [&>svg]:h-4">{icon}</span>}
            {children}
        </RDropdown.Item>
    );
}

/** Item de seleção múltipla — mantém o menu aberto ao marcar/desmarcar. */
export function DropdownCheckboxItem({
    checked,
    onCheckedChange,
    children,
}: {
    checked: boolean;
    onCheckedChange: (checked: boolean) => void;
    children: React.ReactNode;
}) {
    return (
        <RDropdown.CheckboxItem
            checked={checked}
            onCheckedChange={onCheckedChange}
            onSelect={(e) => e.preventDefault()}
            className="relative flex cursor-pointer select-none items-center gap-2 rounded-md py-2 touch:min-h-touch pl-8 pr-3 text-sm text-slate-700 dark:text-zinc-200 outline-none data-[highlighted]:bg-slate-100 dark:data-[highlighted]:bg-night_input transition-colors"
        >
            <RDropdown.ItemIndicator className="absolute left-2 inline-flex items-center text-dark_green dark:text-green_on_dark">
                <Check size={15} strokeWidth={2.5} />
            </RDropdown.ItemIndicator>
            {children}
        </RDropdown.CheckboxItem>
    );
}

function DropdownSeparator() {
    return <RDropdown.Separator className="my-1 h-px bg-slate-100 dark:bg-white/[0.06]" />;
}
